import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import LevelBadge from './LevelBadge';
import VerificationBadge, { RiskScoreBadge } from './VerificationBadge';
import { useReview } from '../../context/ReviewContext';

const StoreCard = ({ store }) => {
  const { t } = useTranslation();
  const { getStoreReviewStats } = useReview();
  const [reviewStats, setReviewStats] = useState({ averageRating: 0, totalReviews: 0 });

  useEffect(() => {
    const loadReviewStats = async () => {
      if (store?.id) {
        try {
          const stats = await getStoreReviewStats(store.id);
          setReviewStats(stats);
        } catch (error) {
          console.error('Error loading store review stats:', error);
        }
      }
    };
    loadReviewStats();
  }, [store?.id, getStoreReviewStats]);
  return (
    <Link to={`/marketplace/stores/${store.id}`} className="block">
      <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
        {/* Cover Image */}
        <div className="relative h-20 md:h-32 bg-gradient-to-br from-green-400 to-green-600">
          {store.coverImage ? (
            <img
              src={store.coverImage}
              alt={store.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white text-xl md:text-3xl">
              🏪
            </div>
          )}
          
          {/* Logo */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
            {store.logo ? (
              <img
                src={store.logo}
                alt={store.name}
                className="w-10 h-10 md:w-16 md:h-16 rounded-full border-2 md:border-4 border-white object-cover"
                onError={(e) => {
                  e.target.src = '/logo.png';
                }}
              />
            ) : (
              <img
                src="/logo.png"
                alt="CannaTrade Logo"
                className="w-10 h-10 md:w-16 md:h-16 rounded-full border-2 md:border-4 border-white object-contain bg-white p-1"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
          </div>
        </div>

        {/* Store Info */}
        <div className="pt-6 md:pt-8 pb-3 md:pb-4 px-2 md:px-4 text-center flex-1 flex flex-col">
          <h3 className="text-sm md:text-lg font-semibold text-gray-800 mb-1 md:mb-2 line-clamp-1">
            {store.name}
          </h3>
          
          {/* Level Badge */}
          {store.reputationLevel && (
            <div className="mb-1 md:mb-2 flex justify-center">
              <div className="scale-75 md:scale-100">
                <LevelBadge level={store.reputationLevel} score={store.reputationScore} type="store" />
              </div>
            </div>
          )}

          {/* Description - Hidden on mobile */}
          {store.description && (
            <p className="hidden md:block text-sm text-gray-600 mb-2 md:mb-3 line-clamp-2">
              {store.description}
            </p>
          )}

          {/* Verification, Risk Score, and Stats - All in one row */}
          <div className="flex items-center justify-center gap-1.5 md:gap-2 flex-wrap text-xs md:text-sm mt-auto">
            {/* Verification Badge */}
            {store.verification ? (
              <div className="scale-75 md:scale-90 origin-center">
                <VerificationBadge verification={store.verification} size="sm" />
              </div>
            ) : (
              <div className="scale-75 md:scale-90 origin-center">
                <VerificationBadge verification={null} size="sm" />
              </div>
            )}
            
            {/* Risk Score Badge */}
            {store.verification?.riskScore !== undefined && (
              <div className="scale-75 md:scale-90 origin-center">
                <RiskScoreBadge riskScore={store.verification.riskScore} size="sm" />
              </div>
            )}

            {/* Stats - Orders */}
            <div className="text-gray-600">
              <span className="font-semibold">{store.completedOrders || 0}</span>
              <span className="ml-0.5 hidden sm:inline">{t('orders.title')}</span>
            </div>

            {/* Stats - Rating */}
            <div className="flex items-center gap-0.5 text-gray-600">
              <span className="font-semibold">
                {reviewStats.averageRating > 0 ? reviewStats.averageRating.toFixed(1) : (store.averageRating?.toFixed(1) || '0.0')}
              </span>
              <span className="text-yellow-500">★</span>
              {reviewStats.totalReviews > 0 && (
                <span className="text-gray-500">({reviewStats.totalReviews})</span>
              )}
            </div>
          </div>

          {/* Category - Hidden on mobile */}
          {store.category && (
            <div className="hidden md:block mt-2 md:mt-3">
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                {store.category}
              </span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default StoreCard;


